import { Box, Stack } from '@mui/system'
import { Tooltip } from '@mui/material'
import { type FC, type MouseEvent, useEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { FaceDetectionType, type OutdoorEngagement, PersonDetectionType } from '../../../../../api/models'
import { type EngagementRecordings, type RecordingViewedDetails, ViewedIn } from '../types'
import type { EngagementRecordingVideoElement } from '../InspectPlayer'
import { clusterDetections } from './detectionClustering'

// Displayed Footage Highlight Bar - background per viewed state (per the "Interaction Details" Figma note).
// --shape-navy-high/low aren't in this app's palette yet, so they resolve to the fallback hex; --bg-surface-low
// is the shared "solid gray" (also used by the clip segments on the track above).
const VIEWED_IN_BACKGROUND: Record<ViewedIn, string> = {
  [ViewedIn.Main]: 'var(--shape-navy)', // dark blue - displayed full size (Feed / Investigate main)
  [ViewedIn.Mini]: 'var(--shape-navy-var)', // light blue - only ever shown in the mini player
  [ViewedIn.NotViewed]: 'var(--bg-surface-low)', // solid gray - never displayed to the specialist
  // striped gray - never available to the specialist (Video Verification extra footage)
  [ViewedIn.NotAvailable]: 'repeating-linear-gradient(45deg, white 0, white 2px, var(--shape-navy-var) 2px, var(--shape-navy-var) 4px)'
}

// Tooltip copy per viewed state (Figma "Interaction Details" hover states). Keep in sync with the legend in
// docs/testing/Ert-Inspect.md.
const VIEWED_IN_TOOLTIP_LABEL: Record<ViewedIn, string> = {
  [ViewedIn.Main]: 'Main Player',
  [ViewedIn.Mini]: 'Mini Player Only',
  [ViewedIn.NotViewed]: 'Not Viewed',
  [ViewedIn.NotAvailable]: 'Customer Only'
}

interface CameraTimelineProps {
  cameraId: string
  startTime: string
  isActive: boolean
  // The camera's currently-playing video element and which clip it's playing, for the live scrubber.
  // Null until the camera's player has mounted and resolved a source.
  activeVideoElement: EngagementRecordingVideoElement | null
  recordings: EngagementRecordings
  engagement: OutdoorEngagement
  isSelected: boolean
  relativeStartTimePercent: number | null
  onClick: (absoluteTimeMs: number) => void
  // Per-clip "displayed footage" details driving the viewed bar's four states (from getRecordingViewedDetails).
  cameraViewedDetails: RecordingViewedDetails[]
}

export const CameraTimeline: FC<CameraTimelineProps> = ({
  cameraId,
  startTime,
  activeVideoElement,
  recordings,
  engagement,
  isSelected,
  relativeStartTimePercent,
  onClick,
  cameraViewedDetails
}) => {
  const [scrubberPercent, setScrubberPercent] = useState<number | null>(null)
  const [clickPercent, setClickPercent] = useState<number | null>(null)
  const previousVideoElRef = useRef<HTMLVideoElement | null>(null)

  const earliestMs = recordings.earliestStartTime ? DateTime.fromISO(recordings.earliestStartTime).toMillis() : null
  const latestMs = recordings.latestEndTime ? DateTime.fromISO(recordings.latestEndTime).toMillis() : null
  const startMs = startTime ? DateTime.fromISO(startTime).toMillis() : null
  const totalDurationMs = earliestMs !== null && latestMs !== null ? latestMs - earliestMs : null
  const displayScrubberPercent = scrubberPercent ?? clickPercent

  // The gray band starts at whichever comes first: the engagement start or this camera's first footage
  // (preroll can precede the engagement). It always reaches the right edge - the axis is capped at the
  // engagement end (capRecordingsAtEngagementEnd), so nothing can lie beyond it.
  const leftGrayPercent = Math.min(
    relativeStartTimePercent ?? 0,
    startMs !== null && earliestMs !== null && totalDurationMs ? ((startMs - earliestMs) / totalDurationMs) * 100 : 0
  )

  const trackBackground = totalDurationMs
    ? `linear-gradient(to right, white ${leftGrayPercent}%, var(--neutral-50) ${leftGrayPercent}%)`
    : 'var(--neutral-50)'

  const cameraRecordings = recordings.recordingsByCameraId.get(cameraId) ?? []

  // Displayed-footage segments for the viewed bar, one per clip, positioned by absolute time and colored by
  // how the specialist saw it (main / mini / not-viewed / not-available).
  const viewedBarSegments =
    earliestMs !== null && totalDurationMs
      ? cameraViewedDetails.map((detail, i) => {
          const startPercent = Math.max(0, Math.min(100, ((detail.startTimeEpoch - earliestMs) / totalDurationMs) * 100))
          const endPercent = Math.max(0, Math.min(100, ((detail.endTimeEpoch - earliestMs) / totalDurationMs) * 100))
          return { key: `${detail.startTimeEpoch}-${i}`, startPercent, widthPercent: endPercent - startPercent, viewedIn: detail.viewedIn }
        })
      : []

  // Face/person detections for this camera, each at its absolute time, grouped into clusters so a burst
  // of nearby detections renders as one count marker instead of a crowd of overlapping pills (mirrors
  // holodeck). Colour follows the cluster's dominant type; position is the cluster's average time.
  const cameraDetections =
    earliestMs !== null && totalDurationMs
      ? engagement.videoRecordings
          .filter(vr => vr.cameraId === cameraId)
          .flatMap(vr => {
            const recordingStartMs = DateTime.fromISO(vr.timestamp).toMillis()
            return (vr.analysis?.detections ?? [])
              .filter(detection => detection.type === FaceDetectionType.Face || detection.type === PersonDetectionType.Person)
              .map(detection => ({ id: detection.id, type: detection.type, timestampMs: recordingStartMs + detection.ptsSeconds * 1000 }))
          })
      : []

  // the 1s threshold matches holodeck's
  const detectionClusters =
    earliestMs !== null && totalDurationMs
      ? clusterDetections(cameraDetections, 1).map(cluster => ({
          id: cluster.id,
          count: cluster.count,
          primaryType: cluster.primaryType,
          timestampMs: cluster.timestampMs,
          percent: Math.max(0, Math.min(100, ((cluster.timestampMs - earliestMs) / totalDurationMs) * 100))
        }))
      : []

  // Each clip's footage window, positioned across the engagement timeline. Drawing these shows where the
  // (often sparse) clips actually are, so the user can click within one to seek - without them the track
  // is a blank bar and most clicks land in gaps that snap to a clip start.
  const clipSegments =
    earliestMs !== null && totalDurationMs
      ? cameraRecordings.map(clip => {
          const clipStartMs = DateTime.fromISO(clip.startTime).toMillis()
          const clipEndMs = DateTime.fromISO(clip.endTime).toMillis()
          const startPercent = Math.max(0, Math.min(100, ((clipStartMs - earliestMs) / totalDurationMs) * 100))
          const endPercent = Math.max(0, Math.min(100, ((clipEndMs - earliestMs) / totalDurationMs) * 100))
          return { key: clip.recordingId, startPercent, widthPercent: endPercent - startPercent, clipStartMs, clipEndMs }
        })
      : []

  // Only the clip segments are clickable (not the blank gaps between them). The click maps to an
  // absolute time within the clicked clip, so seeking always lands inside real footage.
  const onClipSeek = (e: MouseEvent<HTMLDivElement>, clipStartMs: number, clipEndMs: number) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const absoluteTimeMs = clipStartMs + fraction * (clipEndMs - clipStartMs)
    if (earliestMs !== null && totalDurationMs) {
      setClickPercent(((absoluteTimeMs - earliestMs) / totalDurationMs) * 100)
    }
    onClick(absoluteTimeMs)
  }

  // Clicking a detection marker seeks to that cluster's time, like holodeck.
  const handleDetectionClick = (e: MouseEvent<HTMLDivElement>, clusterMs: number, percent: number) => {
    e.stopPropagation()
    setClickPercent(percent)
    onClick(clusterMs)
  }

  useEffect(() => {
    const videoEl = activeVideoElement?.videoEl
    const clipStartTime = activeVideoElement?.recording.startTime
    const isSourceSwapping = activeVideoElement?.isSourceSwapping ?? false
    if (!videoEl || !clipStartTime || earliestMs === null || !totalDurationMs) {
      previousVideoElRef.current = videoEl ?? null
      return
    }

    // video.currentTime is relative to the CURRENT clip's start, not the camera's earliest clip,
    // so the playhead's absolute time is the current clip's start plus currentTime.
    const clipStartMs = DateTime.fromISO(clipStartTime).toMillis()
    const updateScrubber = () => {
      // Mid-swap, the element's currentTime (e.g. the transient reset to ~0 while the next clip's source
      // attaches) belongs to no clip - hold the scrubber until the swap lands (a seeked/timeupdate
      // follows). Gating on the swap flag rather than videoEl.duration matters for FLV: mpegts.js reports
      // no duration until the whole file has downloaded, and the clip plays fine that entire time.
      if (isSourceSwapping) return
      const absoluteTimeMs = clipStartMs + videoEl.currentTime * 1000
      const percent = ((absoluteTimeMs - earliestMs) / totalDurationMs) * 100
      setScrubberPercent(Math.max(0, Math.min(100, percent)))
    }

    // Reposition immediately only when the element itself changed (camera switch / first ready), where
    // currentTime already matches this clip. On a same-element clip change (auto-advance or a cross-clip
    // seek) the recording only flips once the swapped-in source is ready (InspectPlayerForCamera exposes
    // the PLAYING clip, not the seek target), so currentTime is already valid for it - but a seeked/
    // timeupdate is imminent at that point, so waiting for it keeps this effect simple.
    if (videoEl !== previousVideoElRef.current) {
      updateScrubber()
    }
    previousVideoElRef.current = videoEl

    videoEl.addEventListener('timeupdate', updateScrubber)
    videoEl.addEventListener('seeked', updateScrubber)
    return () => {
      videoEl.removeEventListener('timeupdate', updateScrubber)
      videoEl.removeEventListener('seeked', updateScrubber)
    }
  }, [activeVideoElement, earliestMs, totalDurationMs])

  useEffect(() => {
    if (!isSelected) setClickPercent(null)
  }, [isSelected])

  return (
    <Stack direction="column" sx={{ gap: 'var(--spacing-2xs)' }}>
      <Stack
        direction="row"
        sx={{
          height: '1.5rem',
          position: 'relative',
          background: trackBackground,
          border: isSelected ? '1px solid var(--yellow-600)' : '1px solid var(--neutral-200)',
          borderTopLeftRadius: 'var(--border-radius-xl)',
          borderBottomLeftRadius: 'var(--border-radius-xl)',
          overflow: 'hidden'
        }}
        data-testid="camera-timeline"
      >
        <Box data-testid="camera-timeline-clip-segments">
          {clipSegments.map(({ key, startPercent, widthPercent }) => (
            <Box
              key={key}
              sx={{
                position: 'absolute',
                left: `${startPercent}%`,
                width: `${widthPercent}%`,
                top: 0,
                bottom: 0,
                background: 'var(--bg-surface-low)',
                borderLeft: '1px solid var(--border-neutral)',
                borderRight: '1px solid var(--border-neutral)',
                cursor: 'pointer'
              }}
              data-testid="camera-timeline-clip-segment"
            />
          ))}
        </Box>
        <Box data-testid="camera-timeline-detection-markers">
          {detectionClusters.map(({ id, count, primaryType, percent, timestampMs }) => {
            const isFaceDetection = primaryType === FaceDetectionType.Face
            // Width grows with the count (capped), matching holodeck's ClusterMarker sizing.
            const width = count === 1 ? 10 : 8 + Math.min(count, 12) * 2
            return (
              <Box
                key={id}
                onClick={e => handleDetectionClick(e, timestampMs, percent)}
                sx={{
                  position: 'absolute',
                  left: `${percent}%`,
                  top: '0',
                  height: '100%',
                  width: `${width}px`,
                  borderRadius: 'var(--border-radius-xl)',
                  backgroundColor: isFaceDetection ? 'var(--green-500)' : 'var(--blue-500)',
                  color: 'var(--text-white-fixed)',
                  fontWeight: 'bold',
                  fontSize: 'var(--size-2xs)',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 10
                }}
                data-testid="camera-timeline-detection-marker"
              >
                {count >= 10 ? '10+' : count}
              </Box>
            )
          })}
        </Box>
        <Box>
          {/* an invisible but still clickable segment overlaying each clip, so a click seeks within real footage */}
          {clipSegments.map(({ key, startPercent, widthPercent, clipStartMs, clipEndMs }) => (
            <Box
              key={key}
              onClick={e => onClipSeek(e, clipStartMs, clipEndMs)}
              sx={{
                position: 'absolute',
                left: `${startPercent}%`,
                width: `${widthPercent}%`,
                top: 0,
                bottom: 0,
                background: 'none',
                cursor: 'pointer'
              }}
              // keyed by recordingId (`${cameraId}:${startTime}`) so a test can click a specific clip
              data-testid={`camera-timeline-clip-seek-${key}`}
            />
          ))}
        </Box>
        {isSelected && displayScrubberPercent !== null && (
          <Box
            style={{ left: `${displayScrubberPercent}%` }}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              width: '2px',
              background: 'var(--neutral-500)',
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
              zIndex: 10
            }}
            data-testid="camera-timeline-scrubber"
          />
        )}
      </Stack>

      <Stack
        direction="row"
        sx={{
          height: '0.75rem', // taller than the visible 0.25rem strip - the extra space is the segments' hover/tap target
          position: 'relative',
          overflow: 'hidden'
        }}
        data-testid="camera-viewed-bar"
      >
        {viewedBarSegments.map(({ key, startPercent, widthPercent, viewedIn }) => (
          <Tooltip key={key} title={VIEWED_IN_TOOLTIP_LABEL[viewedIn]} placement="top">
            <Box
              data-testid={`camera-viewed-segment-${viewedIn}`}
              sx={{
                position: 'absolute',
                left: `${startPercent}%`,
                width: `${widthPercent}%`,
                top: 0,
                bottom: 0,
                height: '0.25rem',
                background: VIEWED_IN_BACKGROUND[viewedIn]
              }}
            />
          </Tooltip>
        ))}
      </Stack>
    </Stack>
  )
}

CameraTimeline.displayName = 'CameraTimeline'
